/**
 * Agent of Agents — SSE stream consumer.
 *
 * The transport is the platform's one `streamSSE` (`@services/sse`, which
 * this module's loop became); what stays here is the meta-agent's entry
 * points and their event types.
 *
 * Every entry point accepts an optional `AbortSignal`. It is handed to
 * `fetch`, which is what makes Stop instantaneous: aborting rejects the
 * in-flight `reader.read()` right away and tears the socket down, rather
 * than waiting for the next SSE event to arrive so a loop-side flag can be
 * polled. That matters here because the meta-agent can sit silent inside a
 * single long tool call (a simulation exchange) for tens of seconds, and
 * the keepalives it emits meanwhile are SSE comments that never surface as
 * events. Server-side the socket close is the stop signal: it trips
 * `progress_bus.pump_stream`'s `abandoned` flag, which closes the producer
 * and cancels the turn.
 */

import type { DriveFileRef } from '@/types/models';
import api from '@services/api';
import { fileRefusalFromBody } from '@modules/conversations/services/fileValidationService';
import { apiErrorFromBody } from '@modules/conversations/services/conversationService';
import { streamSSE } from '@services/sse';
import type { StreamEvent } from '../types';

/** Options every AoA stream accepts. */
export interface AoAStreamOptions {
  signal?: AbortSignal;
}

const consumeSseStream = (
  url: string,
  body: Record<string, unknown>,
  options?: AoAStreamOptions,
) => streamSSE<StreamEvent>(url, body, options);

export function streamAoAMessage(
  conversationId: string,
  payload: { message: string; provider?: string; model?: string },
  options?: AoAStreamOptions,
): AsyncGenerator<StreamEvent, void, undefined> {
  return consumeSseStream(
    `/agent-of-agents/conversations/${conversationId}/stream`,
    payload,
    options,
  );
}

/** Continue an answer the output cap paused (the conversation's latest
 *  message): what the model writes streams as `ai_chunk`s that belong to
 *  THAT message; no user message is written. */
export function continueAoAMessage(
  conversationId: string,
  messageId: string,
  options?: AoAStreamOptions,
): AsyncGenerator<StreamEvent, void, undefined> {
  return consumeSseStream(
    `/agent-of-agents/conversations/${conversationId}/messages/${messageId}/continue`,
    {},
    options,
  );
}

/** Stream a turn with file attachments. Multipart upload — the backend
 *  validates each file against the same tenant limits regular chat uses
 *  (file type, size, total batch size, conversation total file count)
 *  then persists them via the dedup service and threads the file_ids
 *  into send_message_stream so the meta-agent gets knowledge_search /
 *  read_file / excel_* / pptx_* tools auto-wired for the uploaded files. */
export async function* streamAoAMessageWithFiles(
  conversationId: string,
  payload: { message: string; provider?: string; model?: string; files: File[]; drive_files?: DriveFileRef[] },
  options?: AoAStreamOptions,
): AsyncGenerator<StreamEvent, void, undefined> {
  const apiUrl = api.defaults.baseURL;
  if (!apiUrl) throw new Error('API baseURL not configured');
  const token = sessionStorage.getItem('access_token');
  const tenantId = sessionStorage.getItem('tenant_id') || '';

  const form = new FormData();
  form.append('message', payload.message);
  if (payload.provider) form.append('provider', payload.provider);
  if (payload.model) form.append('model', payload.model);
  for (const file of payload.files) form.append('files', file);
  if (payload.drive_files?.length) form.append('drive_files', JSON.stringify(payload.drive_files.map((f) => f.file_id)));

  const response = await fetch(
    `${apiUrl}/agent-of-agents/conversations/${conversationId}/stream-with-files`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
        // No Content-Type — the browser sets multipart boundary itself.
      },
      body: form,
      signal: options?.signal,
    },
  );

  if (!response.ok) {
    // Refused before the stream opened, as in every chat: a refused file
    // (type, size, count…) is an UploadError the panel words in the user's
    // language; anything else an ApiError carrying the server's sentence.
    const errorText = await response.text();
    throw fileRefusalFromBody(errorText) ?? apiErrorFromBody(response, errorText, 'AoA stream with files error');
  }
  if (!response.body) throw new Error('No response body');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const jsonStr = line.slice(6);
        if (!jsonStr.trim()) continue;
        try {
          const ev = JSON.parse(jsonStr) as StreamEvent;
          yield ev;
          if (ev.type === 'done') return;
        } catch {
          /* ignore malformed lines */
        }
      }
    }
  } finally {
    try {
      reader.cancel();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Step 3 of the AoA improvement plan — Accept-Continue.
 *
 * After the user clicks Accept (or Edit) on a proposal card and
 * /proposals/:id/resolve returns {auto_continue: true}, the chat panel
 * immediately opens this stream so the meta-agent resumes guidance
 * without the user typing. The backend route persists a synthetic
 * resolution event tagged with is_continuation_marker, so the model
 * sees the change (including any user edits) and the visible chat is
 * not polluted by the synthetic message text.
 *
 * Yields the same StreamEvent shapes as streamAoAMessage; the panel
 * can pipe both through the same timeline reducer.
 */
export function streamAoAContinuationFromProposal(
  conversationId: string,
  payload: { proposal_id: string; provider?: string; model?: string },
  options?: AoAStreamOptions,
): AsyncGenerator<StreamEvent, void, undefined> {
  return consumeSseStream(
    `/agent-of-agents/conversations/${conversationId}/continue-from-proposal`,
    payload,
    options,
  );
}
